import cn from 'classnames'
import {FC, useEffect, useRef} from 'react'

import {PulseIcon} from '@/components/common/icons/PulseIcon'
import {Tooltip} from '@/components/common/tooltip/Tooltip'
import {Link} from '@/i18n'
import {theme} from '@/theme/theme'
import {QueryLink} from '@/utils/constants'

import {NumberBadge} from '../NumberBadge'
import {Text, TextFontWeight, TextVariant} from '../Text'

type TabContentProps = {
  title: string
  badgeNumber?: number
  hasPulseIcon: boolean
  isActive: boolean
  textColor: string
}

const TabContent: FC<TabContentProps> = ({
  title,
  hasPulseIcon,
  badgeNumber,
  isActive,
  textColor,
}) => (
  <>
    <div className="content">
      <Text
        variant={TextVariant.subtitle13}
        fontWeight={isActive ? TextFontWeight.bold : TextFontWeight.regular}
        color={textColor}
      >
        {title}
      </Text>
      {badgeNumber && badgeNumber > 0 ? (
        <span className="badge">
          <NumberBadge number={badgeNumber} backgroundColor={theme.palette.red[500]} />
        </span>
      ) : null}
      {hasPulseIcon && <PulseIcon size={6} circleColor={theme.palette.error.main} />}
    </div>

    <style jsx>{`
      .content {
        display: flex;
        justify-content: center;
        gap: ${theme.spacing[1.5]};
      }
      .badge {
        display: block;
        margin-top: -${theme.spacing[0.5]};
      }
    `}</style>
  </>
)

type TabProps = {
  title: string
  isActive: boolean
  linkHref?: QueryLink | string
  // The tab is a page of its own rather than a panel swapped in on the current one, so it is
  // announced as a link on the page it points at, not as a button.
  isPageLink?: boolean
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
  badgeNumber?: number
  isDisabled?: boolean
  isFullWidth?: boolean
  tooltip?: string
  hasIndicator?: boolean
}

export const Tab: FC<React.PropsWithChildren<TabProps>> = ({
  title,
  isActive,
  linkHref,
  isPageLink = false,
  onClick,
  badgeNumber,
  isDisabled,
  isFullWidth = false,
  tooltip,
  hasIndicator = false,
}) => {
  const ref = useRef<HTMLLIElement>(null)

  /*
   * A page tab arrives on a freshly mounted tab bar, scrolled back to the start, so on a narrow
   * screen the tab just tapped is off to the right. Centre it instead. Left alone where the bar
   * swaps a panel on the same page: nothing remounts there.
   */
  useEffect(() => {
    const track = ref.current?.closest<HTMLElement>('.tabs-wrapper')

    if (!isPageLink || !isActive || !track || !ref.current) {
      return
    }

    track.scrollLeft = ref.current.offsetLeft - (track.clientWidth - ref.current.offsetWidth) / 2
  }, [isActive, isPageLink])

  const textColor = isActive
    ? theme.palette.text.default
    : isDisabled
    ? theme.palette.gray[400]
    : theme.palette.text.light

  const formattedContent = tooltip ? (
    <Tooltip tooltip={tooltip}>
      <TabContent
        isActive={isActive}
        textColor={textColor}
        title={title}
        badgeNumber={badgeNumber}
        hasPulseIcon={!badgeNumber && hasIndicator}
      />
    </Tooltip>
  ) : (
    <TabContent
      isActive={isActive}
      textColor={textColor}
      title={title}
      badgeNumber={badgeNumber}
      hasPulseIcon={!badgeNumber && hasIndicator}
    />
  )

  return (
    <>
      <li
        ref={ref}
        className={cn(
          'tab',
          isActive && 'active',
          isDisabled && 'disabled',
          isFullWidth && 'full-width',
        )}
      >
        {linkHref && !isDisabled ? (
          <Link href={linkHref} scroll={false} legacyBehavior>
            <a
              className="wrapper"
              role={isPageLink ? 'link' : 'button'}
              tabIndex={0}
              onClick={onClick}
              aria-current={isActive ? (isPageLink ? 'page' : true) : undefined}
            >
              {formattedContent}
            </a>
          </Link>
        ) : (
          <button
            type="button"
            className="wrapper"
            onClick={isDisabled ? undefined : onClick}
            aria-disabled={isDisabled}
            aria-current={isActive || undefined}
          >
            {formattedContent}
          </button>
        )}
      </li>

      <style jsx>{`
        .tab {
          display: flex;
          align-items: center;
          position: relative;
          white-space: nowrap;
        }
        .tab.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: ${theme.palette.gray[900]};
          height: 2px;
          border-radius: 4px;
          z-index: 1;
        }
        .tab::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: ${theme.palette.gray[200]};
          height: 2px;
          border-radius: 4px;
        }
        .wrapper {
          padding: ${theme.spacing[3]} ${theme.spacing[2.5]};
          cursor: pointer;
          position: relative;
        }
        .wrapper:focus-visible {
          outline-offset: -2px;
          z-index: 2;
        }
        .badge {
          margin-left: ${theme.spacing[1.5]};
        }
        .disabled .wrapper {
          cursor: not-allowed;
        }
        .full-width {
          flex-grow: 1;
        }
        .full-width .wrapper {
          width: 100%;
          justify-content: center;
        }
      `}</style>
    </>
  )
}
